import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import {
  ROUTES_PATH,
  casaConstants,
  dashboardTypes,
  toastMessageConstants
} from '../../../constants/globalConstant';
import { usePreferences } from '../../../hooks/usePreferences';
import { hierarchyToLinear } from '../../../utils/globalUtils';
import { teamsViewSortItems } from '../../../utils/casaUtils';
import { addHomepageMenuItems } from '../../../utils/casaWebUtils';
import useToast from '../../../hooks/useToast';
import { useDashboardConfig } from '../../../queries/useDashboardConfig';
import { useTeamsHierarchy, teamsKey } from '../../../queries/useTeams';
import { teamsViewSteps } from '../../../components/Coach/coachSteps';
import { useWebTeamsDashboards, useWebTeamsLaunchers } from './hooks';
import { useTeamsFilters } from '../shared/hooks';
import { TeamsPageLayout, TeamsHeader, TeamsTableWrapper } from '../shared/components';
import { buildWebTeamsColumnDefs } from './config/columnDefs';
import { getAllTeamsDataForTable } from './utility';
import { API_CREATE_SIERA_WEB_HOMEPAGE } from '../../../api/webHompageConfigurationServices';
import SieraWebHomepage from '../../../components/Modal/SieraWebHomepage';
import TableListingShimmer from '../../../components/reusableComponents/listing/TableListing/Shimmer';
import { WebTeamsDataContext } from './WebTeamsDataContext';
import styles from './styles.module.css';

const WebTeams = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  // State
  const teamsViewSortState = useState({
    sortColumn: teamsViewSortItems[0].id,
    sortOrder: 1,
    label: teamsViewSortItems[0].label
  });
  const searchState = useState('');
  const [dialog, setDialog] = useState({ state: false, dialogBox: {} });
  const [customDashboardForm, setCustomDashboardForm] = useState(false);
  const [addPageTeamId, setAddPageTeamId] = useState(null);

  // Data fetching
  const teamsHierarchy = useTeamsHierarchy();
  const { mutate } = useSWRConfig();
  const {
    dashboardsById,
    dashboards,
    loading: dashboardsLoading,
    refetchDashboards
  } = useWebTeamsDashboards();
  const { launchersById, launchers, loading: launchersLoading } = useWebTeamsLaunchers();
  const { preferences, updatePreference } = usePreferences();
  const { data: appConfig } = useDashboardConfig();
  const teamsCoachMarksVisited = preferences?.teamsCoachMarksVisited;
  const sideNavigationCoachMarksVisited = preferences?.sideNavigationCoachMarksVisited;

  const loading = dashboardsLoading || launchersLoading;

  // Refetch function for team assignments
  const refetchTeamsV2 = useCallback(async () => {
    mutate('/dashboard-teams');
  }, [mutate]);

  // Data transformation
  const linearTeams = useMemo(() => {
    return hierarchyToLinear(teamsHierarchy);
  }, [teamsHierarchy]);

  const teamsMappedWithTeamId = useMemo(() => {
    return Object.fromEntries(linearTeams.map((team) => [team.teamId, team]));
  }, [linearTeams]);

  const defaultDashboards = dashboards?.defaultDashboards || {};
  const webTeamDashboards = dashboards?.teamDashboards || [];
  const defaultLauncher = launchers?.defaultLauncher || {};
  const teamLaunchers = launchers?.teamLaunchers || [];

  const dashboardsbyTeamId = useMemo(() => {
    return Object.fromEntries(webTeamDashboards.map((item) => [item.teamId, item]));
  }, [webTeamDashboards]);

  const launchersbyTeamId = useMemo(() => {
    return Object.fromEntries(teamLaunchers.map((item) => [item.teamId, item]));
  }, [teamLaunchers]);

  const tableData = useMemo(() => {
    return getAllTeamsDataForTable(
      linearTeams,
      dashboardsbyTeamId,
      launchersbyTeamId,
      defaultDashboards,
      defaultLauncher
    );
  }, [linearTeams, dashboardsbyTeamId, launchersbyTeamId, defaultDashboards, defaultLauncher]);

  // Filter and sort teams
  let filterAndSortedTeams = useTeamsFilters(
    tableData.teamsData,
    tableData.defaultTeam,
    searchState[0],
    teamsViewSortState[0].label,
    teamsMappedWithTeamId
  );

  // Apply recently modified filter
  if (teamsViewSortState[0].label === casaConstants.RECENTLY_MODIFIED) {
    filterAndSortedTeams = filterAndSortedTeams.filter(
      (item) => (item.dashboards && item.dashboards.length > 0) || item.launcher !== undefined
    );
  }

  // Platform filter
  const platformFilterItems = useMemo(
    () => [
      { value: 'mobile', label: 'Mobile' },
      { value: 'web', label: 'Web' }
    ],
    []
  );

  const selectedPlatform =
    location.pathname === ROUTES_PATH.webTeams
      ? { value: 'web', label: 'Web' }
      : { value: 'mobile', label: 'Mobile' };

  const onPlatformSelect = (option) => {
    if (option?.value === 'mobile') {
      navigate(ROUTES_PATH.teams);
    } else {
      navigate(ROUTES_PATH.webTeams);
    }
  };

  // Handlers
  const saveSieraDashboard = (data) => {
    setCustomDashboardForm(false);
    setAddPageTeamId(null);
    API_CREATE_SIERA_WEB_HOMEPAGE({
      name: data.name || '',
      layout: { url: data.url },
      teamIds: [data.teamId],
      type: dashboardTypes.SIERA
    }).then((response) => {
      if (response?.status === 200) {
        refetchTeamsV2();
        refetchDashboards();
        toast.success(toastMessageConstants.SIERA_DASHBOARD_PUBLISH_SUCCESS);
      } else {
        toast.error(toastMessageConstants.SIERA_DASHBOARD_PUBLISH_ERROR);
      }
    });
  };

  const menuItemClickHandler = (e, label, teamId) => {
    e.stopPropagation();
    switch (label) {
      case casaConstants.CREATE_SIERA_HOMEPAGE:
        setCustomDashboardForm(true);
        setAddPageTeamId(teamId);
        break;
      case casaConstants.CREATE_NEW_HOMEPAGE:
        navigate(ROUTES_PATH.webHomepageBuilder);
        break;
      default:
        // No action for unhandled menu items.
        break;
    }
  };

  // Column definitions
  const columnDefs = useMemo(
    () =>
      buildWebTeamsColumnDefs({
        dashboardsById,
        launchersById,
        setDialog,
        addHomepageMenuItems,
        onMenuItemClick: menuItemClickHandler
      }),
    [dashboardsById, launchersById]
  );

  useEffect(() => {
    mutate(teamsKey);
  }, [mutate]);

  const onCoachMarkComplete = () => {
    updatePreference({ teamsCoachMarksVisited: true });
  };

  // Modals configuration
  const modals = [
    {
      state: customDashboardForm,
      backgroundStyle: {
        background: 'rgba(var(--ng-neutral-90), 0.5)',
        backdropFilter: 'blur(22px)'
      },
      content: (
        <SieraWebHomepage
          state={customDashboardForm}
          onCancelClick={() => {
            setCustomDashboardForm(false);
            setAddPageTeamId(null);
          }}
          onSaveClick={saveSieraDashboard}
          teamId={addPageTeamId}
          currentItem={{ url: '', name: '' }}
        />
      )
    },
    {
      state: dialog.state,
      backgroundStyle: {
        background: 'rgba(var(--ng-neutral-90), 0.5)',
        backdropFilter: 'blur(22px)'
      },
      content: dialog.dialogBox
    }
  ];

  return (
    <WebTeamsDataContext.Provider value={{ dashboardsById, refetchDashboards, refetchTeamsV2 }}>
      <TeamsPageLayout
        loading={loading}
        showCoachMarks={sideNavigationCoachMarksVisited && !loading}
        coachMarksVisited={teamsCoachMarksVisited}
        coachMarksSteps={teamsViewSteps}
        onCoachMarkComplete={onCoachMarkComplete}
        modals={modals}
        dataTestId="web-teams-data-view">
        {loading ? (
          <TableListingShimmer columns={3} />
        ) : (
          <>
            <TeamsHeader
              searchState={searchState}
              sortState={teamsViewSortState}
              sortItems={teamsViewSortItems}
              platformFilterItems={
                appConfig?.casaWebEnabled && appConfig?.mobileHomeBuilderEnabled
                  ? platformFilterItems
                  : []
              }
              selectedPlatform={selectedPlatform}
              onPlatformSelect={onPlatformSelect}
            />

            <div className={`flex w-full h-full flex-column overflow-hidden`}>
              <div className={styles['loading-spacer']} />

              <TeamsTableWrapper
                data={filterAndSortedTeams}
                columnDefs={columnDefs}
                loading={loading}
                gridKey="web-teams-table"
                dataTestId="web-teams-table-grid"
              />
            </div>
          </>
        )}
      </TeamsPageLayout>
    </WebTeamsDataContext.Provider>
  );
};

export default WebTeams;
